import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Divider,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { addUpcToSupplierPart, updateUpc, deleteUpc } from '../../services/supplierService';

const UpcCodeManager = ({ supplierId, partId, upcs = [], onUpcChange }) => {
  const [upcDialogOpen, setUpcDialogOpen] = useState(false);
  const [editUpcDialogOpen, setEditUpcDialogOpen] = useState(false);
  const [deleteUpcDialogOpen, setDeleteUpcDialogOpen] = useState(false);
  const [newUpc, setNewUpc] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [selectedUpc, setSelectedUpc] = useState(null);
  const [editUpcValue, setEditUpcValue] = useState('');
  const [editIsPrimary, setEditIsPrimary] = useState(false);
  const [loading, setLoading] = useState(false);

  // Open add UPC dialog
  const handleOpenUpcDialog = () => {
    setNewUpc('');
    setIsPrimary(upcs.length === 0); // Set as primary by default if no UPCs exist
    setUpcDialogOpen(true);
  };

  // Close add UPC dialog
  const handleCloseUpcDialog = () => {
    setUpcDialogOpen(false);
  };

  // Open edit UPC dialog
  const handleOpenEditUpcDialog = (upc) => {
    setSelectedUpc(upc);
    setEditUpcValue(upc.upc_code);
    setEditIsPrimary(upc.is_primary);
    setEditUpcDialogOpen(true);
  };

  // Close edit UPC dialog
  const handleCloseEditUpcDialog = () => {
    setEditUpcDialogOpen(false);
    setSelectedUpc(null);
  };

  // Open delete UPC dialog
  const handleOpenDeleteUpcDialog = (upc) => {
    setSelectedUpc(upc);
    setDeleteUpcDialogOpen(true);
  };

  // Close delete UPC dialog
  const handleCloseDeleteUpcDialog = () => {
    setDeleteUpcDialogOpen(false);
    setSelectedUpc(null);
  };

  // Add new UPC
  const handleAddUpc = async () => {
    if (!newUpc.trim()) {
      toast.error('UPC code cannot be empty');
      return;
    }

    try {
      setLoading(true);
      await addUpcToSupplierPart(supplierId, partId, {
        upc_code: newUpc.trim(),
        is_primary: isPrimary
      });
      
      toast.success('UPC code added successfully');
      handleCloseUpcDialog();
      
      if (onUpcChange) {
        onUpcChange();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to add UPC code');
    } finally {
      setLoading(false);
    }
  };

  // Update UPC
  const handleUpdateUpc = async () => {
    if (!editUpcValue.trim()) {
      toast.error('UPC code cannot be empty');
      return;
    }

    try {
      setLoading(true);
      await updateUpc(supplierId, partId, selectedUpc.id, {
        upc_code: editUpcValue.trim(),
        is_primary: editIsPrimary
      });
      
      toast.success('UPC code updated successfully');
      handleCloseEditUpcDialog();
      
      if (onUpcChange) {
        onUpcChange();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update UPC code');
    } finally {
      setLoading(false);
    }
  };

  // Delete UPC
  const handleDeleteUpc = async () => {
    try {
      setLoading(true);
      await deleteUpc(supplierId, partId, selectedUpc.id);
      
      toast.success('UPC code deleted successfully');
      handleCloseDeleteUpcDialog();
      
      if (onUpcChange) {
        onUpcChange();
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete UPC code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1">UPC Codes</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleOpenUpcDialog}
          variant="outlined"
        >
          Add UPC
        </Button>
      </Box>

      {upcs.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No UPC codes added yet.
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
          <List dense>
            {upcs.map((upc) => (
              <ListItem key={upc.id}>
                <ListItemText 
                  primary={upc.upc_code}
                  secondary={
                    upc.is_primary && (
                      <Chip 
                        size="small" 
                        label="Primary" 
                        color="primary" 
                        icon={<CheckIcon />} 
                        variant="outlined"
                      />
                    )
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton 
                    edge="end" 
                    aria-label="edit" 
                    onClick={() => handleOpenEditUpcDialog(upc)}
                    size="small"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    edge="end" 
                    aria-label="delete" 
                    onClick={() => handleOpenDeleteUpcDialog(upc)}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Add UPC Dialog */}
      <Dialog open={upcDialogOpen} onClose={handleCloseUpcDialog}>
        <DialogTitle>Add UPC Code</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="UPC Code"
            type="text"
            fullWidth
            value={newUpc}
            onChange={(e) => setNewUpc(e.target.value)}
            variant="outlined"
          />
          <FormControlLabel
            control={
              <Switch
                checked={isPrimary}
                onChange={(e) => setIsPrimary(e.target.checked)}
                color="primary"
              />
            }
            label="Set as primary UPC code"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpcDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleAddUpc} 
            color="primary" 
            disabled={loading || !newUpc.trim()}
          >
            {loading ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit UPC Dialog */}
      <Dialog open={editUpcDialogOpen} onClose={handleCloseEditUpcDialog}>
        <DialogTitle>Edit UPC Code</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="UPC Code"
            type="text"
            fullWidth
            value={editUpcValue}
            onChange={(e) => setEditUpcValue(e.target.value)}
            variant="outlined"
          />
          <FormControlLabel
            control={
              <Switch
                checked={editIsPrimary}
                onChange={(e) => setEditIsPrimary(e.target.checked)}
                color="primary"
              />
            }
            label="Set as primary UPC code"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditUpcDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleUpdateUpc} 
            color="primary" 
            disabled={loading || !editUpcValue.trim()}
          >
            {loading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete UPC Dialog */}
      <Dialog open={deleteUpcDialogOpen} onClose={handleCloseDeleteUpcDialog}>
        <DialogTitle>Delete UPC Code</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this UPC code? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteUpcDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteUpc} 
            color="error" 
            disabled={loading}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UpcCodeManager;
